import React, { useState, useEffect } from 'react';
import { Calendar, Plus, Trash2, Briefcase, AlertCircle } from 'lucide-react';
import { api } from '../utils/api';
import { formatDateThai } from '../utils/helpers';

const ExtraWorkDaysManagement = () => {
  const [extraDays, setExtraDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showAddModal, setShowAddModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Form State
  const [newDate, setNewDate] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    loadData();
  }, [selectedYear]);

  const loadData = async () => {
    setLoading(true);
    try {
      // ⚠️ Backend จะกรองข้อมูลและส่งกลับมาเฉพาะ "วันทำงานพิเศษ" ของโรงเรียนที่แอดมินสังกัดอยู่เท่านั้น
      const data = await api.getExtraWorkDays(selectedYear);
      setExtraDays(data);
    } catch (error) {
      console.error("Failed to load extra work days", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newDate || !description) {
      alert("กรุณากรอกข้อมูลให้ครบถ้วน");
      return;
    }

    setSubmitting(true);
    try {
      // ⚠️ Backend จะผูก organization_id ของแอดมินคนนี้ เข้ากับวันทำงานพิเศษที่เพิ่งสร้างให้อัตโนมัติ
      await api.addExtraWorkDay({
        work_date: newDate,
        description: description
      });
      
      setNewDate('');
      setDescription('');
      setShowAddModal(false);
      loadData();
      alert('เพิ่มวันทำงานพิเศษเรียบร้อยแล้ว');
    } catch (error) {
      console.error("Failed to add extra work day", error);
      alert('ไม่สามารถเพิ่มได้: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, dateStr) => {
    if (window.confirm(`คุณต้องการลบวันทำงานพิเศษวันที่ ${formatDateThai(dateStr)} ใช่หรือไม่?`)) {
      try {
        // ⚠️ Backend จะป้องกันความปลอดภัย โดยอนุญาตให้ลบได้เฉพาะข้อมูลของโรงเรียนตัวเองเท่านั้น
        await api.deleteExtraWorkDay(id);
        loadData();
      } catch (error) {
        console.error("Failed to delete", error);
        alert('ไม่สามารถลบได้: ' + error.message);
      }
    }
  };

  const years = Array.from({length: 5}, (_, i) => new Date().getFullYear() - 2 + i);

  if (loading && extraDays.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Briefcase className="h-8 w-8 text-indigo-600" />
            วันทำงานพิเศษ
          </h1>
          <p className="text-gray-500 mt-1">จัดการวันที่ต้องมาทำงานในวันหยุด (เสาร์-อาทิตย์)</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-indigo-600 text-white px-4 py-2.5 rounded-lg hover:bg-indigo-700 flex items-center justify-center space-x-2 transition-colors shadow-sm"
        >
          <Plus className="h-5 w-5" />
          <span>เพิ่มวันทำงาน</span>
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-4 bg-gray-50/50">
          <h2 className="text-lg font-semibold text-gray-800">รายการวันทำงานพิเศษ</h2>
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-600">ปีปฏิทิน:</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
            >
              {years.map(year => (
                <option key={year} value={year}>{year + 543}</option>
              ))}
            </select>
          </div>
        </div>

        {extraDays.length === 0 ? (
          <div className="text-center py-16 px-4">
            <div className="bg-indigo-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar className="h-8 w-8 text-indigo-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">ไม่พบข้อมูลวันทำงานพิเศษ</h3>
            <p className="text-gray-500">ในปี พ.ศ. {selectedYear + 543} ยังไม่มีการกำหนดวันทำงานพิเศษ</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 text-gray-600 text-sm border-b border-gray-100">
                  <th className="px-6 py-4 font-semibold">วันที่</th>
                  <th className="px-6 py-4 font-semibold">รายละเอียดกิจกรรม</th>
                  <th className="px-6 py-4 font-semibold text-center w-24">จัดการ</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {extraDays.map((day) => (
                  <tr key={day.id} className="hover:bg-indigo-50/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="bg-indigo-100 text-indigo-700 p-2 rounded-lg">
                          <Calendar className="h-5 w-5" />
                        </div>
                        <span className="font-medium text-gray-900">{formatDateThai(day.work_date)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      {day.description || <span className="text-gray-400 italic">ไม่มีรายละเอียด</span>}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => handleDelete(day.id, day.work_date)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-colors"
                        title="ลบวันทำงาน"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
              <h3 className="text-lg font-bold text-gray-900">เพิ่มวันทำงานพิเศษ</h3>
            </div>
            
            <div className="p-6 space-y-5">
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-100 flex gap-3 text-sm text-blue-800">
                <AlertCircle className="h-5 w-5 flex-shrink-0 text-blue-600" />
                <p>กำหนดวันที่บุคลากร <strong>ต้องมาลงเวลาปฏิบัติงาน</strong> ในวันหยุดสุดสัปดาห์ (ระบบจะไม่นับว่าขาดงานในวันเหล่านี้)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">วันที่ *</label>
                <input
                  type="date"
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-shadow"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">รายละเอียดกิจกรรม *</label>
                <textarea
                  rows="3"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-shadow"
                  placeholder="เช่น กิจกรรมกีฬาสี, ประชุมผู้ปกครอง..."
                ></textarea>
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 rounded-b-lg flex gap-3 justify-end">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium transition-colors"
              >
                ยกเลิก
              </button>
              <button
                onClick={handleAdd}
                disabled={submitting}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 font-medium shadow-sm hover:shadow transition-all disabled:opacity-70 flex items-center gap-2"
              >
                {submitting && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                บันทึก
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExtraWorkDaysManagement;